import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import { mdiEmailFastOutline, mdiEmailOutline, mdiHeadset, mdiMessageTextOutline } from "@mdi/js"
import { parseContactAddress } from "../../../../../../_shared/_logic/contact-addresses"
import { buildContactPointMetaTooltipFields } from "../../../../../../_shared/_logic/contact-quality-tags"
import { formatPropertyCustomerRole } from "../../../../../../_shared/_logic/customer-roles"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../_shared/_logic/rich-tooltips"
import { displayContactChannelLabel } from "../../../../../../../../_shared/_logic/outreach-presentation"

export const normalizeString = (value: unknown) => {
  if (value === null || value === undefined) return null

  const text = typeof value === "string" ? value : String(value)
  const trimmed = text.trim()

  return trimmed ? trimmed : null
}

type ContactAddressInput = Parameters<typeof parseContactAddress>[0]

const asContactAddressInput = (value: unknown): ContactAddressInput =>
  value === null ||
  value === undefined ||
  typeof value === "string" ||
  (typeof value === "object" && !Array.isArray(value))
    ? (value as ContactAddressInput)
    : null

const normalizeTargetKind = (value: unknown) => normalizeString(value)?.toUpperCase() ?? null

const germanCountryLabels = new Set(["de", "deu", "deutschland", "germany"])
const unitOnlyPattern = /^(wohnung|wohneinheit|einheit)\b/i
const postalLocalityPattern = /\b(?:\d{4,6}|[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2})\b/i

const structuredPostalAddress = (value: unknown) => {
  if (value && typeof value === "object" && !Array.isArray(value))
    return parseContactAddress(asContactAddressInput(value))

  if (typeof value !== "string" || !value.trim().startsWith("{")) return null

  const parsed = parseContactAddress(value)

  return parsed.street || parsed.house_number || parsed.zip || parsed.city || parsed.address_extra
    ? parsed
    : null
}

const legacyPostalAddress = (value: string) => {
  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !unitOnlyPattern.test(part))
  const last = parts.at(-1) ?? null
  const previous = parts.at(-2) ?? null
  const lastIsGermanCountry = Boolean(
    last && germanCountryLabels.has(last.toLocaleLowerCase("de-DE")),
  )
  const lastIsForeignCountry = Boolean(
    last &&
    previous &&
    parts.length >= 3 &&
    !/\d/.test(last) &&
    postalLocalityPattern.test(previous),
  )
  const country = lastIsGermanCountry || lastIsForeignCountry ? last : null
  const addressParts = country ? parts.slice(0, -1) : parts
  const localityIndex = addressParts.findLastIndex((part) => postalLocalityPattern.test(part))
  const locality = localityIndex > 0 ? addressParts[localityIndex] : (addressParts.at(-1) ?? null)
  const street = addressParts[0] ?? null
  const addressExtra = localityIndex > 1 ? addressParts.slice(1, localityIndex).join(", ") : null

  return {
    compact: normalizeString(
      [street, locality && locality !== street ? locality : null].filter(Boolean).join(", "),
    ),
    addressExtra,
    country,
  }
}

const postalAddressDetails = (value: unknown) => {
  const structured = structuredPostalAddress(value)

  if (structured) {
    const streetLine = [structured.street, structured.house_number].filter(Boolean).join(" ")
    const cityLine = [structured.zip, structured.city].filter(Boolean).join(" ")

    return {
      compact: normalizeString([streetLine, cityLine].filter(Boolean).join(", ")),
      addressExtra: normalizeString(structured.address_extra),
      country: normalizeString(structured.country),
    }
  }

  const text = normalizeString(value)

  if (!text || text === "[object Object]")
    return { compact: null, addressExtra: null, country: null }

  return legacyPostalAddress(text)
}

/** Visible postal target used by the communication table. */
export const formatCompactPostalAddress = (value: unknown): string | null => {
  return postalAddressDetails(value).compact
}

const isAddressTarget = (
  channel: string | null | undefined,
  candidate?: Record<string, unknown> | null,
  contactPoint?: ContactPoint | null,
) => {
  const normalizedChannel = String(channel || "").toUpperCase()

  if (normalizedChannel === "LETTER") return true

  return [
    candidate?.kind,
    candidate?.contact_point_kind,
    candidate?.target_kind,
    candidate?.value_kind,
    contactPoint?.kind,
  ].some((kind) => normalizeTargetKind(kind) === "ADDRESS")
}

const firstPresentTargetValue = (...values: unknown[]) =>
  values.find((value) => Boolean(normalizeString(value))) ?? null

const formatTargetValue = ({
  channel,
  value,
  candidate,
  contactPoint,
}: {
  channel: string | null | undefined
  value: unknown
  candidate?: Record<string, unknown> | null
  contactPoint?: ContactPoint | null
}) => {
  if (value === null || value === undefined) return null

  const rawText = normalizeString(value)

  if (!isAddressTarget(channel, candidate, contactPoint)) return rawText

  return formatCompactPostalAddress(value)
}

const resolveTargetValue = (
  candidate: Record<string, unknown> | null | undefined,
  channel: string | null | undefined,
  contactPoint?: ContactPoint | null,
) =>
  formatTargetValue({
    channel,
    candidate,
    contactPoint,
    value: firstPresentTargetValue(candidate?.value, candidate?.normalized_value),
  })

const contactPointTargetValue = (
  contactPointId: string | null | undefined,
  contactPointById: Record<string, ContactPoint>,
  channel: string | null | undefined,
  candidate?: Record<string, unknown> | null,
) => {
  if (!contactPointId) return null

  const contactPoint = contactPointById[contactPointId]

  if (!contactPoint) return null

  return formatTargetValue({
    channel,
    candidate,
    contactPoint,
    value: firstPresentTargetValue(contactPoint.value, contactPoint.normalized_value),
  })
}

export const channelTargetLabel = (
  channel: string | null | undefined,
  candidate: Record<string, unknown> | null,
  contactPointId: string | null | undefined,
  contactPointById: Record<string, ContactPoint>,
) => {
  const normalizedChannel = String(channel || "").toUpperCase()
  const contactPoint = contactPointId ? (contactPointById[contactPointId] ?? null) : null
  const candidateValue = candidate ? resolveTargetValue(candidate, channel, contactPoint) : null

  if (candidateValue) return candidateValue

  const contactPointValue = contactPointTargetValue(
    contactPointId,
    contactPointById,
    channel,
    candidate,
  )

  if (contactPointValue) return contactPointValue

  if (normalizedChannel === "LETTER") return "Terminbrief"

  return displayContactChannelLabel(normalizedChannel)
}

const contactPointLifecycleLabel = (value: string | null | undefined) => {
  const normalized = normalizeString(value)?.toUpperCase() || "ACTIVE"

  if (normalized === "ACTIVE") return "Aktiv"

  if (normalized === "CANDIDATE") return "Kandidat"

  if (normalized === "STALE_CANDIDATE") return "Veraltet"

  if (normalized === "INVALID") return "Ungültig"

  if (normalized === "DO_NOT_USE") return "Nicht verwenden"

  if (normalized === "ARCHIVED") return "Archiviert"

  return "Status unbekannt"
}

const unusableContactPointLifecycleStatuses = new Set([
  "CANDIDATE",
  "STALE_CANDIDATE",
  "INVALID",
  "DO_NOT_USE",
  "ARCHIVED",
])

const candidateValue = (candidate: Record<string, unknown> | null | undefined, key: string) =>
  candidate ? normalizeString(candidate[key]) : null

export const contactPointLifecycleStatus = (
  contactPoint?: ContactPoint | null,
  candidate?: Record<string, unknown> | null,
) =>
  candidateValue(candidate, "lifecycle_status") ||
  normalizeString(contactPoint?.lifecycle_status) ||
  "ACTIVE"

export const isUsableContactPointTarget = (
  contactPoint?: ContactPoint | null,
  candidate?: Record<string, unknown> | null,
) => {
  if (typeof candidate?.is_usable === "boolean") return candidate.is_usable

  return !unusableContactPointLifecycleStatuses.has(
    contactPointLifecycleStatus(contactPoint, candidate).toUpperCase(),
  )
}

export const contactPointTooltipMetadata = (
  contactPoint?: ContactPoint | null,
  candidate?: Record<string, unknown> | null,
) => {
  if (!contactPoint && !candidate) return null

  const lifecycleStatus = contactPointLifecycleStatus(contactPoint, candidate)
  const contactPointRecord = (contactPoint ?? {}) as Record<string, unknown>
  const isVerified = Boolean(
    typeof candidate?.is_verified === "boolean" ? candidate.is_verified : contactPoint?.is_verified,
  )
  const metaPoint = {
    ...(contactPoint ?? {}),
    source: candidateValue(candidate, "source") || contactPoint?.source,
    source_import_draft_id:
      candidateValue(candidate, "source_import_draft_id") ||
      normalizeString(contactPointRecord.source_import_draft_id),
    last_seen_import_draft_id:
      candidateValue(candidate, "last_seen_import_draft_id") ||
      normalizeString(contactPointRecord.last_seen_import_draft_id),
    last_seen_in_import_at:
      candidateValue(candidate, "last_seen_in_import_at") ||
      normalizeString(contactPointRecord.last_seen_in_import_at),
    source_record_key:
      candidateValue(candidate, "source_record_key") ||
      normalizeString(contactPointRecord.source_record_key),
    is_verified: isVerified,
  }

  return [
    `Kontaktpunkt: ${isVerified ? "verifiziert" : "nicht verifiziert"}`,
    ...buildContactPointMetaTooltipFields(metaPoint).map(
      (field) => `${field.label}: ${field.value}`,
    ),
    `Status: ${contactPointLifecycleLabel(lifecycleStatus)}`,
    contactPoint?.is_primary ? "Primärer Kontaktpunkt" : null,
  ]
    .filter(Boolean)
    .join("\n")
}

export const channelIcon = (channel: string | null | undefined) => {
  const normalizedChannel = String(channel || "").toUpperCase()

  if (normalizedChannel === "EMAIL") return mdiEmailFastOutline

  if (normalizedChannel === "MOBILE_MESSAGE" || normalizedChannel === "WEB_BOT")
    return mdiMessageTextOutline

  if (normalizedChannel === "PHONE" || normalizedChannel === "VOICE_BOT") return mdiHeadset

  if (normalizedChannel === "LETTER") return mdiEmailOutline

  return mdiMessageTextOutline
}

const candidateCustomerName = (
  candidate: Record<string, unknown>,
  customerById: Record<string, Customer>,
  contactPointById: Record<string, ContactPoint>,
  primaryRecipientName: string | null,
) => {
  const customerId = String(candidate.customer_id ?? "")
  const directName = typeof candidate.customer_name === "string" ? candidate.customer_name : null

  if (directName) return directName

  if (customerId && customerById[customerId]?.display_name)
    return customerById[customerId].display_name

  const contactPointId = String(candidate.contact_point_id ?? "")
  const linkedCustomerId = contactPointById[contactPointId]?.customer_id

  if (linkedCustomerId && customerById[linkedCustomerId]?.display_name) {
    return customerById[linkedCustomerId].display_name
  }

  return primaryRecipientName || "–"
}

const normalizeRecipientName = (value: string | null | undefined) => {
  const text = normalizeString(value)

  return text && text !== "–" ? text : null
}

export const truncateRecipientName = (value: string | null | undefined, maxLength = 12) => {
  const text = normalizeRecipientName(value)

  if (!text) return null

  if (text.length <= maxLength) return text

  return `${text.slice(0, maxLength).trimEnd()}...`
}

const formatRecipientIdentityLabel = (
  roleLabel: string | null | undefined,
  name: string | null | undefined,
) => {
  const normalizedRole = normalizeString(roleLabel) || "Ansprechpartner"
  const shortName = truncateRecipientName(name)

  return shortName ? `${normalizedRole} (${shortName})` : normalizedRole
}

const contactPointSummaryLine = (
  contactPointId: string | null | undefined,
  customerById: Record<string, Customer>,
  contactPointById: Record<string, ContactPoint>,
  primaryRecipientName: string | null,
) => {
  if (!contactPointId) return primaryRecipientName || "–"

  const contactPoint = contactPointById[contactPointId]

  if (!contactPoint) return primaryRecipientName || "–"

  const customerName = contactPoint.customer_id
    ? customerById[contactPoint.customer_id]?.display_name
    : null

  return customerName || primaryRecipientName || "–"
}

export const recipientSubLabel = (
  candidate: Record<string, unknown> | null,
  contactPointId: string | null | undefined,
  customerById: Record<string, Customer>,
  contactPointById: Record<string, ContactPoint>,
  primaryRecipientName: string | null,
) => {
  const role = candidate ? normalizeString(candidate.role) : null
  const roleLabel = role ? formatPropertyCustomerRole(role) : null
  const name = candidate
    ? candidateCustomerName(candidate, customerById, contactPointById, primaryRecipientName)
    : contactPointSummaryLine(contactPointId, customerById, contactPointById, primaryRecipientName)

  return formatRecipientIdentityLabel(roleLabel, name)
}

export const recipientColor = (
  targetLabel: string | null | undefined,
  isUsable: boolean,
): StatusDefinition["color"] => {
  if (!isUsable) return "rose"

  const normalizedTarget = normalizeString(targetLabel)

  if (!normalizedTarget || normalizedTarget === "–") return "orange"

  return "emerald"
}

export type RecipientTagPresentation = {
  label: string
  subLabel: string
  nameLabel: string | null
  targetLabel: string | null
  tooltipHtml: string
  icon: string
  color: StatusDefinition["color"]
  isUsable: boolean
}

export const buildRecipientTagPresentation = ({
  channel,
  candidate = null,
  contactPointId,
  customerById = {},
  contactPointById = {},
  primaryRecipientName = null,
  explicitName = null,
  explicitAddress = null,
  workOrderPropertyAddress = null,
  isUsable = true,
}: {
  channel: string | null | undefined
  candidate?: Record<string, unknown> | null
  contactPointId?: string | null
  customerById?: Record<string, Customer>
  contactPointById?: Record<string, ContactPoint>
  primaryRecipientName?: string | null
  explicitName?: string | null
  explicitAddress?: string | null
  workOrderPropertyAddress?: string | null
  isUsable?: boolean
}): RecipientTagPresentation => {
  const normalizedChannel = String(channel || "").toUpperCase()
  const contactPoint = contactPointId ? (contactPointById[contactPointId] ?? null) : null
  const rawTargetValue = firstPresentTargetValue(
    explicitAddress,
    candidate?.value,
    candidate?.normalized_value,
    contactPoint?.value,
    contactPoint?.normalized_value,
  )
  const addressDetails = isAddressTarget(channel, candidate, contactPoint)
    ? postalAddressDetails(rawTargetValue)
    : null
  const targetValue =
    formatTargetValue({ channel, value: explicitAddress, candidate, contactPoint }) ||
    (candidate ? resolveTargetValue(candidate, channel, contactPoint) : null) ||
    contactPointTargetValue(contactPointId, contactPointById, channel, candidate)
  const candidateName = candidate
    ? normalizeRecipientName(
        candidateCustomerName(candidate, customerById, contactPointById, primaryRecipientName),
      )
    : null
  const contactPointCustomerName = contactPoint?.customer_id
    ? normalizeRecipientName(customerById[contactPoint.customer_id]?.display_name)
    : null
  const recipientName =
    normalizeRecipientName(explicitName) ||
    candidateName ||
    contactPointCustomerName ||
    normalizeRecipientName(primaryRecipientName)
  const fallbackLabel =
    normalizedChannel === "LETTER"
      ? "Terminbrief"
      : normalizeString(displayContactChannelLabel(normalizedChannel)) || "Empfänger"
  const propertyTarget =
    normalizedChannel === "LETTER" && workOrderPropertyAddress
      ? formatCompactPostalAddress(workOrderPropertyAddress) || workOrderPropertyAddress
      : null
  const resolvedTarget = targetValue || propertyTarget
  const label = recipientName || resolvedTarget || fallbackLabel
  const role = candidate ? normalizeString(candidate.role) : null
  const roleLabel = role ? formatPropertyCustomerRole(role) : null
  const subLabel =
    resolvedTarget && resolvedTarget !== label
      ? resolvedTarget
      : roleLabel && recipientName
        ? roleLabel
        : normalizedChannel === "LETTER" && workOrderPropertyAddress
          ? formatCompactPostalAddress(workOrderPropertyAddress) || workOrderPropertyAddress
          : fallbackLabel
  const tooltipHtml = recipientTooltip({
    channel,
    targetLabel: targetValue || label,
    subLabel: recipientName || subLabel || "–",
    workOrderPropertyAddress,
    addressExtra: addressDetails?.addressExtra ?? null,
    country: addressDetails?.country ?? null,
    contactPoint,
    candidate,
    isUsable,
  })

  return {
    label,
    subLabel,
    nameLabel: recipientName,
    targetLabel: resolvedTarget,
    tooltipHtml,
    icon: channelIcon(channel),
    color: recipientColor(resolvedTarget || label, isUsable),
    isUsable,
  }
}

export const recipientTooltip = ({
  channel,
  addressExtra,
  country,
  candidate,
  isUsable,
}: {
  channel: string | null | undefined
  targetLabel: string
  subLabel: string
  workOrderPropertyAddress: string | null
  addressExtra?: string | null
  country?: string | null
  contactPoint?: ContactPoint | null
  candidate?: Record<string, unknown> | null
  isUsable: boolean
}) => {
  const normalizedChannel = String(channel || "").toUpperCase()
  const countryLabel = normalizeString(country)
  const visibleCountry =
    countryLabel && !germanCountryLabels.has(countryLabel.toLocaleLowerCase("de-DE"))
      ? countryLabel
      : null

  return buildRichTooltip([
    normalizedChannel === "LETTER" && addressExtra
      ? tooltipField("Adresszusatz", addressExtra)
      : null,
    normalizedChannel === "LETTER" && visibleCountry ? tooltipField("Land", visibleCountry) : null,
    isUsable
      ? null
      : tooltipText(
          "Dieser Kontaktweg kann derzeit nicht verwendet werden. Bitte die Kontaktdaten prüfen.",
        ),
  ])
}
